
import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Info, 
  Home, 
  Wheat, 
  Heart, 
  GraduationCap,
  Users,
  Droplets,
  Zap,
  Building,
  Recycle,
  Sparkles,
  CloudSun,
  Fish,
  Leaf,
  Scale,
  Network,
  Handshake
} from "lucide-react";
import { sdgsData } from "@/data/sdgs";
import { Button } from "@/components/ui/button";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";

// Map SDG IDs to appropriate icons
const sdgIcons = {
  1: Home,
  2: Wheat,
  3: Heart,
  4: GraduationCap,
  5: Users,
  6: Droplets,
  7: Zap,
  8: Building,
  9: Network,
  10: Scale,
  11: Building,
  12: Recycle,
  13: CloudSun,
  14: Fish,
  15: Leaf,
  16: Scale,
  17: Handshake
};

const SDGGrid: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <section id="sdgs" className="py-16 bg-palsustain-cream">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-palsustain-green mb-4">
            {t("home.sdgs.title")}
          </h2>
          <p className="text-lg text-palsustain-charcoal/80 max-w-3xl mx-auto">
            {t("home.sdgs.subtitle")}
          </p>
        </div>

        <Carousel className="w-full max-w-6xl mx-auto px-6 relative">
          <CarouselContent>
            {sdgsData.map((sdg) => {
              const IconComponent = sdgIcons[sdg.id as keyof typeof sdgIcons] || Sparkles;
              
              return (
                <CarouselItem key={sdg.id} className="md:basis-1/2 lg:basis-1/4">
                  <div 
                    className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 flex flex-col h-full mx-2"
                    style={{ borderTop: `4px solid ${sdg.color}` }}
                  >
                    <div className="p-6 flex flex-col h-full">
                      <div className="flex items-start mb-4">
                        <div 
                          className="w-14 h-14 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
                          style={{ backgroundColor: `${sdg.color}15` }}
                        >
                          <IconComponent 
                            className="w-8 h-8"
                            style={{ color: sdg.color }}
                          />
                        </div>
                        <div>
                          <span 
                            className="text-sm font-medium px-3 py-1 rounded-full inline-block mb-2" 
                            style={{ backgroundColor: sdg.color, color: 'white' }}
                          >
                            {language === "en" ? `Goal ${sdg.id}` : `الهدف ${sdg.id}`}
                          </span>
                          <h3 className="text-xl font-bold text-palsustain-charcoal">
                            {language === "en" ? sdg.shortTitle.en : sdg.shortTitle.ar}
                          </h3>
                        </div>
                      </div>
                      
                      <p className="text-palsustain-charcoal/80 mb-4 line-clamp-3 flex-grow">
                        {language === "en" ? sdg.description.en : sdg.description.ar}
                      </p>
                      
                      <div className="mt-auto">
                        <Link to={`/sdgs/${sdg.slug}`} className="w-full">
                          <button 
                            className="w-full text-left py-2 px-3 text-sm text-palsustain-green border border-palsustain-green/30 rounded-md hover:bg-palsustain-green/5 transition-colors"
                          >
                            {language === "en" 
                              ? "Discover how this goal is applied in Palestinian context" 
                              : "اكتشف كيف يتم تطبيق هذا الهدف في السياق الفلسطيني"}
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="left-0" />
          <CarouselNext className="right-0" />
        </Carousel>
        
        <div className="text-center mt-10">
          <Link to="/sdgs">
            <Button
              variant="default"
              className="bg-palsustain-green text-white hover:bg-palsustain-green/90"
            >
              {language === "en" ? "View All SDGs" : "عرض جميع أهداف التنمية المستدامة"}
              <ArrowRight className={`ml-2 h-4 w-4 ${language === "ar" ? "transform rotate-180" : ""}`} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SDGGrid;
